import "./index.css";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Card, Form, Input, Select, Button, message, Space, Row, Col, Spin } from "antd";
import { pinyin } from "pinyin-pro";
import { UserOutlined, MailOutlined, BankOutlined, CalendarOutlined, TeamOutlined, CrownOutlined } from "@ant-design/icons";

import MenuLayout from "../../../components/Menu";
import Loading from "../../../components/Loading";
import { useUser } from "../../../contexts/UserContext";
import { supabase } from "../../../lib/supabase";

const { Title, Text } = Typography;
const { Option } = Select;

const AddUserPage = () => {
  const navigate = useNavigate();
  const { userProfile, isAdmin, loading, retryCount } = useUser();
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);



  useEffect(() => {
    if (!loading && !isAdmin) {
      // User doesn't have admin role, redirect to search page
      navigate("/search");
    }
  }, [loading, isAdmin, navigate]);

  // Auto-fill pinyin when Chinese name changes
  const handleNameCnChange = (e) => {
    const chineseName = e.target.value;
    if (chineseName) {
      try {
        // Convert Chinese to pinyin without tone marks
        const pinyinResult = pinyin(chineseName, { 
          toneType: "none",
          type: "string"
        });
        form.setFieldsValue({ name_pinyin: pinyinResult });
      } catch (error) {
        // Silently handle pinyin conversion errors
      }
    }
  };

  // Pre-fill form with user's own information when component mounts
  useEffect(() => {
    if (userProfile && isAdmin) {
      form.setFieldsValue({
        schoolm: userProfile.schoolm,
        schoolm_start_time: userProfile.schoolm_start_time,
        schoolm_class: userProfile.schoolm_class,
        admin_role: null // Default to ordinary user
      });
    }
  }, [userProfile, isAdmin, form]);

  const handleSubmit = async (values) => {
    setSubmitLoading(true);
    
    try {
      // Clean numeric fields before submission
      const cleanedValues = { ...values };
      const numericFields = ['schoolm_start_time', 'schoolm_class'];
      numericFields.forEach(field => {
        if (cleanedValues[field] !== undefined && cleanedValues[field] !== null && cleanedValues[field] !== '') {
          // Clean and parse numeric values, removing any non-numeric characters except negative sign
          const cleanedValue = String(cleanedValues[field]).replace(/[^\d-]/g, '');
          const parsedValue = parseInt(cleanedValue, 10);
          cleanedValues[field] = isNaN(parsedValue) ? null : parsedValue;
        }
      });
      
      const { data, error } = await supabase.rpc("add_user_admin", {
        new_auth_email: cleanedValues.auth_email,
        new_name_cn: cleanedValues.name_cn,
        new_name_pinyin: cleanedValues.name_pinyin,
        new_schoolm: cleanedValues.schoolm,
        new_schoolm_start_time: cleanedValues.schoolm_start_time,
        new_schoolm_class: cleanedValues.schoolm_class,
        new_user_role: cleanedValues.admin_role
      });

      if (error) {
        message.error("添加用户失败，请检查网络连接");
        return;
      }

      if (data?.status === 1) {
        // Call the edge function to create auth user
        try {
          const { data: { session } } = await supabase.auth.getSession();
          
          const response = await fetch('https://drzjezvkopukxcgxzaqo.supabase.co/functions/v1/create_user', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`
            },
            body: JSON.stringify({
              email: values.auth_email,
              admin_role: values.admin_role
            })
          });

          const edgeResult = await response.json();

          if (response.ok) {
            message.success("用户添加成功！用户信息已保存到数据库，登录账户已创建。");
          } else {
            message.warning(`用户信息已保存到数据库，但创建登录账户时出现问题: ${edgeResult.error || '未知错误'}`);
          }
        } catch (edgeError) {
          message.warning("用户信息已保存到数据库，但创建登录账户时出现网络错误");
        }
        
        form.resetFields();
        // Re-set the default values after reset
        if (userProfile) {
          form.setFieldsValue({
            schoolm: userProfile.schoolm,
            schoolm_start_time: userProfile.schoolm_start_time,
            schoolm_class: userProfile.schoolm_class,
            admin_role: null
          });
        }
      } else {
        // Handle specific error cases
        const errorMessages = {
          'NotLoggedInError': '用户未登录',
          'UserNotInDBError': '当前用户不在数据库中',
          'RateLimitExceededError': '请求频率过高，请稍后再试',
          'NotAdminError': '您没有管理员权限',
          'UnknownAdminRoleError': '未知的管理员角色',
          'AdminRoleIncompatibleError': '您的管理员权限不足以添加此用户',
          'AdminRoleHigherOrEqualError': '不能添加权限等级相同或更高的用户',
          'InvalidNewUserRoleError': '新用户角色无效',
          'NewUserAlreadyInDBError': '该邮箱已存在于数据库中'
        };
        
        const errorMessage = errorMessages[data?.error_name] || '添加用户失败';
        message.error(errorMessage);
      }
    } catch (err) {
      message.error("添加用户失败，请稍后再试");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    // Re-set the default values after reset
    if (userProfile) {
      form.setFieldsValue({
        schoolm: userProfile.schoolm,
        schoolm_start_time: userProfile.schoolm_start_time,
        schoolm_class: userProfile.schoolm_class,
        admin_role: null
      });
    }
  };

  if (!loading && !isAdmin) {
    // Don't render anything while redirecting
    return null;
  }

  return (
    <MenuLayout>
      <Spin 
        tip={retryCount > 0 ? `正在重试... (第 ${retryCount} 次尝试)` : "加载中..."} 
        indicator={<Loading />} 
        spinning={loading}
      >
        <div className="admin-page">
          <Card className="admin-card">
            <Title level={2}>添加新用户</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
              在此页面添加新用户到系统。请确保填写所有必填字段。
            </Text>
            
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="add-user-form"
              initialValues={{
                admin_role: null
              }}
            >
            {/* 基本信息 */}
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <Space>
                      <MailOutlined />
                      <Text strong>邮箱地址</Text>
                    </Space>
                  }
                  name="auth_email"
                  rules={[
                    { required: true, message: '请输入邮箱地址' },
                    { type: 'email', message: '请输入有效的邮箱地址' }
                  ]}
                >
                  <Input 
                    size="large"
                    placeholder="请输入邮箱地址"
                    allowClear
                  />
                </Form.Item>
              </Col>
              
              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <Space>
                      <CrownOutlined />
                      <Text strong>管理员角色</Text>
                    </Space>
                  }
                  name="admin_role"
                >
                  <Select
                    size="large"
                    placeholder="请选择管理员角色"
                    allowClear
                  >
                    <Option value={null}>普通用户</Option>
                    <Option value={2}>班级管理员</Option>
                    <Option value={1}>年级管理员</Option>
                    <Option value={0}>超级管理员</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* 姓名信息 */}
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <Space>
                      <UserOutlined />
                      <Text strong>中文姓名</Text>
                    </Space>
                  }
                  name="name_cn"
                  rules={[
                    { required: true, message: '请输入中文姓名' }
                  ]}
                >
                  <Input 
                    size="large"
                    placeholder="请输入中文姓名"
                    allowClear
                    onChange={handleNameCnChange}
                  />
                </Form.Item>
              </Col>
              
              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <Space>
                      <UserOutlined />
                      <Text strong>拼音姓名</Text>
                    </Space>
                  }
                  name="name_pinyin"
                  rules={[
                    { required: true, message: '请输入拼音姓名' }
                  ]}
                >
                  <Input 
                    size="large"
                    placeholder="请输入拼音姓名（自动生成）"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* 学校信息 */}
            <Row gutter={[24, 0]}>
              <Col xs={24}>
                <Form.Item
                  label={
                    <Space>
                      <BankOutlined />
                      <Text strong>初中学校</Text>
                    </Space>
                  }
                  name="schoolm"
                  rules={[
                    { required: true, message: '请输入初中学校' }
                  ]}
                >
                  <Input 
                    size="large"
                    placeholder="请输入初中学校（自动填充）"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <Space>
                      <CalendarOutlined />
                      <Text strong>初中入学年份</Text>
                    </Space>
                  }
                  name="schoolm_start_time"
                  rules={[
                    { required: true, message: '请选择初中入学年份' }
                  ]}
                >
                  <Select
                    size="large"
                    placeholder="请选择初中入学年份"
                    allowClear
                  >
                    {Array.from({ length: 20 }, (_, i) => 2024 - i).map(year => (
                      <Option key={year} value={year}>{year}年</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              
              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <Space>
                      <TeamOutlined />
                      <Text strong>初中班级</Text>
                    </Space>
                  }
                  name="schoolm_class"
                  rules={[
                    { required: true, message: '请选择初中班级' }
                  ]}
                >
                  <Select
                    size="large"
                    placeholder="请选择初中班级"
                    allowClear
                  >
                    {Array.from({ length: 20 }, (_, i) => i + 1).map(classNum => (
                      <Option key={classNum} value={classNum}>{classNum}班</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item style={{ marginTop: '32px', marginBottom: 0 }}>
              <Space size="large">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={submitLoading}
                  style={{ minWidth: '120px' }}
                >
                  添加用户
                </Button>
                <Button
                  size="large"
                  onClick={handleReset}
                  style={{ minWidth: '120px' }}
                >
                  重置表单
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>
      </Spin>
    </MenuLayout>
  );
};

export default AddUserPage; 